import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import React, { useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { PrimaryButtonMediumV2 } from "../../../../../components/Buttons/Buttons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { ToggleSwitch } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { H3 } from "../../../../../components/Typography/Typography";
import { Form } from "../../../../../layout/FormLayout";
import type { OptionType, UUID } from "../../../../../types/types";
import type {
  AttributeTemplateSummarySchema,
  AttributeTemplateSchema,
  TemplateType,
} from "../../../../../types/types.PIM";
import { stringIsNotOnlyWhiteSpaceRegex } from "../../../../../util/regexes";
import type { StorefrontMetaData } from "../../../../../types/types";
import {
  isAxiosError,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";

import * as z from "zod";
import { zodSelectBoxDefault } from "../../../../../util/zod.util";
import { strings } from "../../../../../util/strings";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export function convertTemplateToOption(
  template: AttributeTemplateSummarySchema
): OptionType<UUID> {
  return {
    value: template.template_id,
    label: template.template_name,
  };
}

export const TEMPLATE_TYPE_TOGGLE_MAP: Record<
  TemplateType,
  keyof StorefrontMetaData | null
> = {
  product: null, // always available
  customer: "enable_customer",
  supplier: "enable_supplier",
  distributor: "enable_distributor",
};

type formOutput = {
  template_name: string;
  copy_existing_template: boolean;
  template?: OptionType<UUID>;
};

export function CreateNewTemplateForm({
  templateType,
  onSuccess,
}: {
  templateType: TemplateType;
  onSuccess: (id: UUID) => void;
}) {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const {
    register,
    handleSubmit,
    control,
    formState,
    errors,
    watch,
    setError,
  } = useFormWrapper({
    resolver: zodResolver(
      z
        .object({
          template_name: z
            .string()
            .regex(
              stringIsNotOnlyWhiteSpaceRegex,
              strings(t).thisIsARequiredField
            ),
          copy_existing_template: z.boolean(),
          template: zodSelectBoxDefault(t).optional(),
        })
        .superRefine((data, ctx) => {
          if (
            data.copy_existing_template === true &&
            data.template === undefined
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: strings(t).thisIsARequiredField,
              path: ["template"],
            });
          }
        })
    ),
    defaultValues: {
      copy_existing_template: false,
    },
  });

  const isCopyingTemplate = watch("copy_existing_template") ?? false;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (values: formOutput) => {
    setIsSubmitting(true);
    try {
      const { data: template }: { data: AttributeTemplateSchema } =
        await axios.post(`/v2/tenants/${tenant_id}/pim/templates`, {
          name: values.template_name,
          template_type: templateType,
          ...(values.template && !!values.copy_existing_template
            ? { template_id: values.template.value }
            : null),
        });

      notifySuccess(t("Template created successfully"));
      onSuccess(template.template_id);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setError("template_name", {
            message: t("Template name already exists"),
          });
        }
      } else {
        notifyError(t("There was an error creating the template"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <H3>{t("Create new template")}</H3>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          name="template_name"
          label={t("Template name")}
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />
        <ToggleSwitch
          label={t("Copy existing template")}
          name="copy_existing_template"
          theref={register({ required: false })}
        />
        <div style={{ display: isCopyingTemplate ? "block" : "none" }}>
          <Controller
            control={control}
            name="template"
            rules={{ required: true }}
            render={({ onChange, value }) => (
              <SearchSelectInfiniteScroll
                name="template"
                placeholder={t("Choose template")}
                errors={errors}
                formState={formState}
                isClearable={true}
                menuPosition="fixed"
                baseUrl={
                  isCopyingTemplate && templateType
                    ? `/v2/tenants/${tenant_id}/pim/templates/summary`
                    : null
                }
                params={(() => {
                  const params = new URLSearchParams();
                  if (templateType) {
                    params.append("template_type", templateType);
                  }
                  return params;
                })()}
                getOptions={(response: AttributeTemplateSummarySchema[]) =>
                  response.map((template) => ({
                    value: template.template_id,
                    label: getTemplateDisplayName(template),
                  }))
                }
                value={value}
                onChange={onChange}
              />
            )}
          />
        </div>
        <ButtonContainer>
          <PrimaryButtonMediumV2 type="submit" disabled={isSubmitting}>
            {t("Create")}
          </PrimaryButtonMediumV2>
        </ButtonContainer>
      </Form>
    </>
  );
}
